import React from 'react';
import { useState } from 'react';
import { Skeleton, TabMainNav } from '@sato/components';
import { compact } from 'lodash';
import { observer } from 'mobx-react-lite';

import { Link } from '@components/common/Link';
import { LogoutModal } from '@components/LogoutModal';
import { useLanguageSelection } from '@hooks/useLanguageSelection';
import { ROUTES } from '@routes/routes.constants';
import { apartmentStore } from '@stores/apartmentStore';
import { useAuth } from '@stores/authStore';
import { contractStore } from '@stores/contractStore';
import { t } from '@stores/localization/localization.store';
import { userStore } from '@stores/userStore';
import { isLocalOrTestEnv } from '@utils/featureFlags';

import { useContentfulMainNavigation } from './MainNavigation.hooks';

export const MainNavigation = observer(() => {
  const { isAuthenticated } = useAuth();
  const languageSelectionProps = useLanguageSelection();
  const { isLoading, isError, tabs } = useContentfulMainNavigation();

  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const { firstName } = userStore.user ?? {};

  if (isLoading) {
    return <Skeleton className="h-[5rem] w-full md:h-[9rem]" />;
  }
  // optimistically render main nav in proudction even if fetching Contentful content failed
  else if (isError && isLocalOrTestEnv) {
    throw Error('error fetching MainNavigation');
  }

  if (!isAuthenticated) {
    return (
      <TabMainNav
        menuTitle="OmaSATO"
        linkComponent={Link}
        logoProps={{ to: ROUTES.FRONTPAGE }}
        languageSelection={languageSelectionProps}
        items={[
          {
            label: t('login'),
            icon: 'userOutlined',
            to: ROUTES.LOGIN,
            collapseOnMobile: true,
          },
        ]}
      />
    );
  }

  return (
    <>
      <TabMainNav
        menuTitle="OmaSATO"
        linkComponent={Link}
        className="hide-on-print"
        languageSelection={languageSelectionProps}
        contextSelection={
          !apartmentStore.apartments?.length
            ? undefined
            : apartmentStore.apartments.length === 1
              ? {
                  children: apartmentStore.selectedApartment?.address,
                  icon: 'location',
                  to: ROUTES.APARTMENT,
                }
              : {
                  // TODO: translate
                  'aria-label': 'Choose apartment',
                  icon: 'location24',
                  selectedKey: apartmentStore.selectedApartmentID ?? null,
                  onSelectionChange: (key) => {
                    if (!key) return;

                    apartmentStore.setSelectedApartmentID(key.toString());
                  },
                  items: apartmentStore.apartments?.map(
                    ({ apartmentId, address }) => ({
                      label: address,
                      value: apartmentId,
                    })
                  ),
                }
        }
        items={compact([
          !contractStore.isApartmentSeeker && {
            label: t('messages_title'),
            to: ROUTES.MESSAGES,
            icon: 'message24',
            collapseOnMobile: true,
          },
          {
            label: firstName ?? '',
            // TODO translate
            'aria-label': 'Käyttäjävalinnat',
            icon: 'userFilled',
            collapseOnMobile: true,
            items: [
              {
                title: t('my_info_title'),
                to: ROUTES.MY_INFORMATION,
              },
              {
                title: t('logout'),
                // workaround to not trigger navigation and displaying a dialog
                to: '#',
                onClick: () => setShowLogoutDialog(true),
              },
            ],
          },
        ])}
        tabs={tabs}
        logoProps={{ to: ROUTES.MY_HOME }}
      />

      <LogoutModal
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
      />
    </>
  );
});
